import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
    CircularProgress,
    Chip,
    Paper,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { getRankingReasoning } from '../../api/openaiService';

const PaperReasoningDialog = ({ open, onClose, paperIndex, socketId }) => {
    const [loading, setLoading] = useState(true);
    const [reasoningData, setReasoningData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            if (!socketId) {
                console.error('PaperReasoningDialog: No socketId provided');
                setError('Socket connection is not available. Please try refreshing the page.');
                setLoading(false);
                return;
            }

            fetchReasoningData();
        }
    }, [open, socketId]);

    const fetchReasoningData = async () => {
        setLoading(true);
        setError(null);

        try {
            if (!socketId) {
                throw new Error('Socket ID is not available');
            }

            const response = await getRankingReasoning(socketId);

            if (response.dataAvailable && response.data) {
                setReasoningData(response.data);
            } else {
                setError('No reasoning data available for this session');
            }
        } catch (err) {
            console.error('Error fetching reasoning data:', err);
            setError('Failed to load reasoning data: ' + (err.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    // Get the current paper data if available
    const currentPaper = reasoningData?.scores?.find(paper => paper.paperId === paperIndex + 1);
    const paperReasoning = reasoningData?.reasoning?.[paperIndex + 1];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee'
            }}>
                <Typography variant="h6">
                    AI Ranking Reasoning
                    {currentPaper && (
                        <Chip
                            label={`Score: ${currentPaper.score}`}
                            size="small"
                            color="primary"
                            sx={{ ml: 2 }}
                        />
                    )}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 2 }}>
                {!socketId ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Socket connection is not available. Please refresh the page.
                    </Alert>
                ) : loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress size={40} />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{ p: 3, textAlign: 'center' }}>
                        {error}
                    </Typography>
                ) : !paperReasoning ? (
                    <Typography color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
                        No detailed reasoning available for this paper
                    </Typography>
                ) : (
                    <Box>
                        {currentPaper && (
                            <Box mb={3} p={2} bgcolor="#f8f9fa" borderRadius={1}>
                                <Typography variant="h6" gutterBottom>
                                    {currentPaper.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Relevance Score: {currentPaper.score}/100
                                </Typography>
                            </Box>
                        )}

                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center">
                                    <ThumbUpIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1">Arguments For Including This Paper</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {paperReasoning?.argumentsFor || "No arguments provided for including this paper."}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion defaultExpanded sx={{ mt: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center">
                                    <ThumbDownIcon color="error" sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1">Arguments Against Including This Paper</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {paperReasoning?.argumentsAgainst || "No arguments provided against including this paper."}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ borderTop: '1px solid #eee', px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaperReasoningDialog; 